import type { ClubKits, KitConfig, KitRoleConfig, KitShirtConfig, KitGarmentConfig, KitVisibility } from './team';

const DEFAULT_SHIRT: KitShirtConfig = {
  pattern: 'plain',
  baseColor: '#1d4ed8',
  patternColor: '#ffffff',
  count: 6,
  sleeveColor: '#1d4ed8',
  collarStyle: 'v-neck',
  collarColor: '#ffffff',
};

const DEFAULT_SHORTS: KitGarmentConfig = { baseColor: '#ffffff', trimColor: '' };
const DEFAULT_SOCKS: KitGarmentConfig = { baseColor: '#1d4ed8', trimColor: '' };

const defaultRole = (): KitRoleConfig => ({
  shirt: { ...DEFAULT_SHIRT },
  shorts: { ...DEFAULT_SHORTS },
  socks: { ...DEFAULT_SOCKS },
});

const defaultKit = (): KitConfig => ({ keeper: defaultRole(), outfield: defaultRole() });

function deepFreeze<T>(obj: T): T {
  Object.getOwnPropertyNames(obj).forEach((name) => {
    const value = (obj as Record<string, unknown>)[name];
    if (value && typeof value === 'object') deepFreeze(value);
  });
  return Object.freeze(obj);
}

export const DEFAULT_CLUB_KITS: ClubKits = deepFreeze({ home: defaultKit(), away: defaultKit(), third: defaultKit() });

// Loosely typed input so the backfill can pass a raw Mongo doc.
type LegacyColors = Record<string, unknown>;

const KIT_KEYS = ['home', 'away', 'third'] as const;

const str = (v: unknown, fallback: string): string => (typeof v === 'string' && v.trim().length > 0 ? v : fallback);

export const buildKitsFromLegacyColors = (c: LegacyColors): ClubKits => {
  const result = {
    home: defaultKit(),
    away: defaultKit(),
    third: defaultKit(),
  };

  for (const kit of KIT_KEYS) {
    const keeperShirt = str(c[`${kit}KitKeeperShirtColor`], DEFAULT_SHIRT.baseColor);
    const outfieldShirt = str(c[`${kit}KitOutfieldShirtColor`], DEFAULT_SHIRT.baseColor);
    const shorts = str(c[`${kit}KitShortsColor`], DEFAULT_SHORTS.baseColor);
    const socks = str(c[`${kit}KitSocksColor`], DEFAULT_SOCKS.baseColor);

    result[kit].keeper.shirt.baseColor = keeperShirt;
    result[kit].keeper.shirt.sleeveColor = keeperShirt;
    result[kit].outfield.shirt.baseColor = outfieldShirt;
    result[kit].outfield.shirt.sleeveColor = outfieldShirt;

    result[kit].keeper.shorts.baseColor = shorts;
    result[kit].outfield.shorts.baseColor = shorts;
    result[kit].keeper.socks.baseColor = socks;
    result[kit].outfield.socks.baseColor = socks;
  }

  return result;
};

// --- Kit visibility ---------------------------------------------------------

const roleEqualsDefault = (role: KitRoleConfig, def: KitRoleConfig): boolean =>
  role.shirt.pattern === def.shirt.pattern &&
  role.shirt.baseColor === def.shirt.baseColor &&
  role.shirt.patternColor === def.shirt.patternColor &&
  role.shirt.count === def.shirt.count &&
  role.shirt.sleeveColor === def.shirt.sleeveColor &&
  role.shirt.collarStyle === def.shirt.collarStyle &&
  role.shirt.collarColor === def.shirt.collarColor &&
  role.shorts.baseColor === def.shorts.baseColor &&
  role.shorts.trimColor === def.shorts.trimColor &&
  role.socks.baseColor === def.socks.baseColor &&
  role.socks.trimColor === def.socks.trimColor;

/** True when a kit's designer colours differ from the default template. */
export const isKitCustomised = (kits: ClubKits, key: keyof ClubKits): boolean =>
  !roleEqualsDefault(kits[key].keeper, DEFAULT_CLUB_KITS[key].keeper) ||
  !roleEqualsDefault(kits[key].outfield, DEFAULT_CLUB_KITS[key].outfield);

const hasKitImage = (club: LegacyColors, key: keyof ClubKits): boolean =>
  !!str(club[`${key}Kit`], '') || !!str(club[`${key}KeeperKit`], '');

/**
 * The computed default visibility for a kit when no explicit flag is stored: on
 * when a kit image is uploaded OR the kit designer colours differ from the
 * default template.
 */
export const defaultKitVisibility = (club: LegacyColors, kits: ClubKits, key: keyof ClubKits): boolean =>
  hasKitImage(club, key) || isKitCustomised(kits, key);

/**
 * Resolves whether a kit should be shown: the explicit `kitVisibility` flag when
 * set, otherwise the computed default. `kits` should be the resolved kit config
 * (`club.kits ?? buildKitsFromLegacyColors(club)`).
 */
export const resolveKitVisibility = (club: LegacyColors, kits: ClubKits, key: keyof ClubKits): boolean => {
  const explicit = (club.kitVisibility as KitVisibility | undefined)?.[key];
  return typeof explicit === 'boolean' ? explicit : defaultKitVisibility(club, kits, key);
};
